import React from 'react';

interface PlanFeature {
  text: string;
  highlighted?: boolean;
}

interface Plan {
  name: string;
  subtitle: string;
  setupPrice: string;
  monthlyPrice: string;
  features: PlanFeature[];
  buttonText: string;
  isRecommended?: boolean;
  isDark: boolean;
}

const plans: Plan[] = [
  {
    name: 'EMPIRE',
    subtitle: 'ECOSYSTEM',
    setupPrice: '2.900€',
    monthlyPrice: '390€/mes',
    features: [
      { text: 'App propia iOS/Android', highlighted: true },
      { text: 'Notificaciones Push', highlighted: true },
      { text: 'Marketing de eventos', highlighted: true }
    ],
    buttonText: 'CONSULTAR EMPIRE',
    isDark: true
  },
  {
    name: 'GROWTH',
    subtitle: 'CONTROL & SCALE',
    setupPrice: '1.450€',
    monthlyPrice: '250€/mes',
    features: [
      { text: 'Todo lo de Core' },
      { text: 'Instagram DMs' },
      { text: 'Estrategia Reactivación' },
      { text: 'DASHBOARD CEO COMPLETO', highlighted: true }
    ],
    buttonText: 'EMPEZAR CON GROWTH',
    isRecommended: true,
    isDark: true
  },
  {
    name: 'CORE',
    subtitle: 'EFFICIENCY',
    setupPrice: '950€',
    monthlyPrice: '150€/mes',
    features: [
      { text: 'Mina en WhatsApp 24/7' },
      { text: 'Gestión de citas directa' },
      { text: 'Dashboard básico' }
    ],
    buttonText: 'SOLICITAR CORE',
    isDark: true
  }
];

export const PricingPlansSection: React.FC = () => {
  const handlePlanClick = () => {
    const demoSection = document.getElementById('demo-form');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-32 bg-dark">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-display font-semibold text-white mb-6">
            Escala tu facturación
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Deja de gestionar el caos y empieza a gestionar tu crecimiento.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
          {/* EMPIRE Plan */}
          <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-3xl p-8 hover:border-gray-700 transition-all duration-300">
            <div className="mb-8">
              <h3 className="text-3xl font-display font-bold text-white tracking-wide">
                {plans[0].name}
              </h3>
              <p className="text-xs text-gray-500 tracking-[0.2em] mt-1">
                {plans[0].subtitle}
              </p>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-bold text-white">
                  {plans[0].setupPrice}
                </span>
                <span className="text-sm text-gray-500 uppercase tracking-wider">
                  setup
                </span>
              </div>
              <p className="text-lg text-gray-400 mt-2">
                {plans[0].monthlyPrice}
              </p>
            </div>

            <ul className="space-y-4 mb-10">
              {plans[0].features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <span className="text-amber-400 text-lg">★</span>
                  <span className="text-gray-300 text-sm">
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={handlePlanClick}
              className="w-full py-4 px-6 bg-transparent border border-gray-600 text-white text-sm font-semibold tracking-wider rounded-xl hover:bg-gray-800 hover:border-gray-500 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {plans[0].buttonText}
            </button>
          </div>

          {/* GROWTH Plan - Recommended */}
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-black border border-gray-700 rounded-3xl p-8 hover:border-primary/50 transition-all duration-300 lg:-mt-8">
            {/* Recommended Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-gradient-to-r from-primary to-teal-400 text-white text-xs font-semibold px-4 py-2 rounded-full uppercase tracking-wider whitespace-nowrap">
                Opción Recomendada
              </span>
            </div>

            <div className="mb-8 mt-4">
              <p className="text-xs text-gray-500 tracking-[0.2em] mb-1">
                {plans[1].subtitle}
              </p>
              <h3 className="text-3xl font-display font-bold text-white tracking-wide">
                {plans[1].name}
              </h3>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-bold text-white">
                  {plans[1].setupPrice}
                </span>
                <span className="text-sm text-gray-500 uppercase tracking-wider">
                  setup
                </span>
              </div>
              <p className="text-lg bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-semibold mt-2">
                {plans[1].monthlyPrice}
              </p>
            </div>

            <ul className="space-y-4 mb-10">
              {plans[1].features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <span className="text-primary text-lg">✓</span>
                  <span className={`text-sm ${feature.highlighted ? 'text-white font-semibold' : 'text-gray-300'}`}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={handlePlanClick}
              className="w-full py-4 px-6 bg-gradient-to-r from-cyan-400 to-pink-500 text-white text-sm font-semibold tracking-wider rounded-xl hover:shadow-lg hover:shadow-pink-500/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {plans[1].buttonText}
            </button>
          </div>

          {/* CORE Plan */}
          <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-3xl p-8 hover:border-gray-700 transition-all duration-300">
            <div className="mb-8">
              <h3 className="text-3xl font-display font-bold text-white tracking-wide">
                {plans[2].name}
              </h3>
              <p className="text-xs text-gray-500 tracking-[0.2em] mt-1">
                {plans[2].subtitle}
              </p>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-bold text-white">
                  {plans[2].setupPrice}
                </span>
                <span className="text-sm text-gray-500 uppercase tracking-wider">
                  setup
                </span>
              </div>
              <p className="text-lg bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-semibold mt-2">
                {plans[2].monthlyPrice}
              </p>
            </div>

            <ul className="space-y-4 mb-10">
              {plans[2].features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <span className="text-primary text-lg">◆</span>
                  <span className="text-gray-300 text-sm">
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={handlePlanClick}
              className="w-full py-4 px-6 bg-transparent border border-gray-600 text-white text-sm font-semibold tracking-wider rounded-xl hover:bg-gray-800 hover:border-gray-500 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {plans[2].buttonText}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
